import { useCallback, useMemo } from 'react';

import { useAtom, useSetAtom } from 'jotai';
import { Collapse, SearchBadges } from 'ui';
import { randomId } from 'ui/Hooks';

import {
  tableSearch_InputFormatterAtom,
  tableSearch_InputResetAtom,
  tableSearch_InputResetNotShownInParamsAtom,
} from './Search/store';
import { useTableContext } from './table.context';

const TableSearchBadges = () => {
  const { atomStore, tableSearchObjectAtom } = useTableContext();
  const clearSearchField = useSetAtom(tableSearch_InputResetAtom, { store: atomStore });
  /** Clears all search inputs that are not shown in params  */
  const clearSearchFieldNotShownInParams = useSetAtom(tableSearch_InputResetNotShownInParamsAtom, {
    store: atomStore,
  });
  const getFormattedSearchField = useSetAtom(tableSearch_InputFormatterAtom, { store: atomStore });
  const [searchObject] = useAtom(tableSearchObjectAtom, {
    store: atomStore,
  });

  /** Returns a compatible array of Objects that SearchBadges expects  */
  const searchArray = useMemo(() => {
    try {
      const search = searchObject?.searches;

      if (!search) return [];

      const searchAsArray = Object.entries(search);

      if (!searchAsArray.length) return [];

      let hasCustom = false;

      const searchArray = searchAsArray
        .map(([name, value]) => {
          if (!value) return;
          const searchValue = Object.values(value)[0] as string;
          const formattedValue = getFormattedSearchField({
            field: name,
            value: searchValue,
          });

          // Allow only one custom field to be displayed
          if (formattedValue.showInParams === false) {
            if (hasCustom) return;
            hasCustom = true;
            return {
              label: 'Custom fields filtered',
              value: '',
              originalValue: '',
              name: 'custom',
              id: 'custom',
            };
          }
          if (!formattedValue.value) {
            return;
          }
          return {
            /** Formatted badge label */
            label: formattedValue.label ?? name,
            /** Displayed Badge value */
            value: formattedValue.value,
            /** Original Badge Value */
            originalValue: value,
            /** Non Formatted Name */
            name: name,
            id: randomId(),
          };
        })
        .filter((search) => search !== undefined);

      return searchArray;
    } catch {
      return [];
    }
  }, [getFormattedSearchField, searchObject?.searches]);

  /** Calls Clearing on the provided field */
  const handleSearchBadgeClick = useCallback(
    (key: string) => {
      const searchTarget = searchArray.find((search) => search.id === key);
      if (!searchTarget) return;
      if (searchTarget.id === 'custom') {
        clearSearchFieldNotShownInParams(searchTarget.name);
      } else {
        clearSearchField(searchTarget.name);
      }
    },
    [clearSearchField, clearSearchFieldNotShownInParams, searchArray]
  );

  const isSearchBadgesVisible = useMemo(() => searchArray?.length > 0, [searchArray]);

  return (
    <Collapse
      in={isSearchBadgesVisible}
      mt={isSearchBadgesVisible ? '0px' : '-24px'}
      transitionDuration={300}
      transitionTimingFunction="ease-in-out"
    >
      <SearchBadges search={searchArray} onClick={handleSearchBadgeClick} />
    </Collapse>
  );
};

export { TableSearchBadges };
